import { useState, useEffect, useCallback } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  TextInput,
  Modal,
} from 'react-native';
import { useRouter, Stack } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Organization, OrganizationMembership } from '@/types/database';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { clearOnboardingState } from '@/lib/onboardingState';
import { markSeen } from '@/lib/tutorials';
import {
  MapPin,
  ChevronLeft,
  Users,
  Trophy,
  Dumbbell,
  TrendingUp,
  Crown,
  Megaphone,
  Swords,
  Edit3,
  X,
  Check,
} from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';

const log = createLogger('GYM_PROFILE_WEB');

const ACCENT_PALETTE = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#22c55e', // green
  '#f59e0b', // amber
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
];

interface LeaderboardEntry {
  user_id: string;
  display_name: string;
  handle?: string;
  lifting_score: number;
  duel_standard_strength_kg: number;
  avatar_url?: string;
}

interface GymStats {
  totalMembers: number;
  activeThisWeek: number;
  avgStrengthScore: number;
  totalWorkouts7d: number;
}

export default function GymProfileScreen() {
  const router = useRouter();
  const { user, profile, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const { useLocalSearchParams } = require('expo-router');
  const params = useLocalSearchParams() as { source?: string | string[] };
  const sourceParam = Array.isArray(params?.source) ? params.source[0] : params?.source;
  const isOnboarding = sourceParam === 'onboarding';

  const [org, setOrg] = useState<(Organization & { description?: string; accent_color?: string; logo_url?: string }) | null>(null);
  const [membership, setMembership] = useState<OrganizationMembership | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<GymStats>({ totalMembers: 0, activeThisWeek: 0, avgStrengthScore: 0, totalWorkouts7d: 0 });
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editColor, setEditColor] = useState('');
  const [saving, setSaving] = useState(false);

  const gymId = profile?.gym_id || profile?.home_gym_id;
  const gymName = profile?.home_gym_name || 'My Gym';
  const gymAddress = profile?.home_gym_address;
  const accentColor = org?.accent_color || theme.colors.accent;
  const isAdmin = membership?.role === 'owner' || membership?.role === 'admin';

  const fetchGymData = useCallback(async () => {
    if (!gymId || !user?.id) {
      setLoading(false);
      return;
    }

    try {
      // Fetch org details
      const { data: orgData } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', gymId)
        .maybeSingle();

      if (orgData) setOrg(orgData as Organization);

      // Fetch user's membership
      const { data: memData } = await supabase
        .from('organization_memberships')
        .select('*')
        .eq('org_id', gymId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (memData) setMembership(memData as OrganizationMembership);

      // Fetch member count
      const { count: memberCount } = await supabase
        .from('organization_memberships')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', gymId)
        .eq('status', 'active');

      // Fetch gym_memberships count as fallback
      const { count: gymMemberCount } = await supabase
        .from('gym_memberships')
        .select('*', { count: 'exact', head: true })
        .eq('gym_id', gymId)
        .eq('status', 'active');

      const totalMembers = memberCount || gymMemberCount || 0;

      // Fetch leaderboard from gym_memberships -> profiles
      const { data: members } = await supabase
        .from('gym_memberships')
        .select('user_id, profiles!inner(display_name, handle, lifting_score, duel_standard_strength_kg, avatar_url)')
        .eq('gym_id', gymId)
        .eq('status', 'active')
        .order('profiles(lifting_score)', { ascending: false })
        .limit(5);

      const leaderboardData: LeaderboardEntry[] = (members || []).map((m: any) => ({
        user_id: m.user_id,
        display_name: m.profiles?.display_name || 'Unknown',
        handle: m.profiles?.handle,
        lifting_score: Math.round(m.profiles?.duel_standard_strength_kg || m.profiles?.lifting_score || 0),
        duel_standard_strength_kg: m.profiles?.duel_standard_strength_kg || 0,
        avatar_url: m.profiles?.avatar_url,
      }));
      // Sort client-side as fallback
      leaderboardData.sort((a, b) => b.duel_standard_strength_kg - a.duel_standard_strength_kg);
      setLeaderboard(leaderboardData);

      setStats({
        totalMembers,
        activeThisWeek: 0, // Would need activity tracking
        avgStrengthScore: leaderboardData.length > 0
          ? Math.round(leaderboardData.reduce((s, e) => s + e.duel_standard_strength_kg, 0) / leaderboardData.length)
          : 0,
        totalWorkouts7d: 0, // Would need workout log query
      });
    } catch (err) {
      log.error('Failed to fetch gym data:', err);
    } finally {
      setLoading(false);
    }
  }, [gymId, user?.id]);

  useEffect(() => {
    void fetchGymData();
  }, [fetchGymData]);

  const handleSaveEdit = async () => {
    if (!org?.id) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          name: editName.trim() || org.name,
          description: editDesc.trim() || null,
          accent_color: editColor || null,
        })
        .eq('id', org.id);

      if (error) {
        log.error('Save error:', error);
      } else {
        setOrg({ ...org, name: editName.trim() || org.name, description: editDesc.trim() || undefined, accent_color: editColor || undefined });
        setEditModalVisible(false);
      }
    } catch (err) {
      log.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = () => {
    setEditName(org?.name || gymName);
    setEditDesc(org?.description || '');
    setEditColor(org?.accent_color || '');
    setEditModalVisible(true);
  };

  // No gym state
  if (!gymId && !profile?.home_gym_name) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.headerBar}>
          <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/')} style={styles.backBtn}>
            <ChevronLeft size={24} color={theme.colors.text} />
          </TouchableOpacity>
          <Text style={[styles.headerBarTitle, { color: theme.colors.text }]}>Home Gym</Text>
          <View style={{ width: 32 }} />
        </View>
        <View style={styles.emptyState}>
          <MapPin size={48} color={theme.colors.textTertiary} />
          <Text style={[styles.emptyTitle, { color: theme.colors.text }]}>No Home Gym Set</Text>
          <Text style={[styles.emptySubtitle, { color: theme.colors.textSecondary }]}>
            Set your home gym to see stats, leaderboards, and connect with members.
          </Text>
          <TouchableOpacity
            style={[styles.primaryBtn, { backgroundColor: theme.colors.accent }]}
            onPress={() => router.push(('/join-gym' + (isOnboarding ? '?source=onboarding' : '')) as any)}>
            <Text style={[styles.primaryBtnText, { color: theme.colors.accentForeground }]}>Find Your Gym</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={async () => {
              if (isOnboarding && user?.id) {
                await supabase.from('profiles').update({ onboarding_completed: true }).eq('id', user.id);
                await clearOnboardingState(user.id);
                await AsyncStorage.setItem('onboarding:walkthrough_complete', 'true');
                await markSeen('home');
                await refreshProfile?.();
                router.replace('/(tabs)');
              } else {
                router.canGoBack() ? router.back() : router.replace('/');
              }
            }}>
            <Text style={{ color: theme.colors.textSecondary, fontSize: 14, textDecorationLine: 'underline', marginTop: 12 }}>
              Skip for now
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: theme.colors.background }]}>
        <Stack.Screen options={{ headerShown: false }} />
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </View>
    );
  }

  const displayName = org?.name || gymName;
  const displayAddress = gymAddress;
  const displayDesc = org?.description;
  const firstLetter = displayName.charAt(0).toUpperCase();

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <Stack.Screen options={{ headerShown: false }} />

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* Header */}
        <View style={[styles.heroSection, { backgroundColor: accentColor + '18' }]}>
          <View style={styles.headerBar}>
            <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/')} style={styles.backBtn}>
              <ChevronLeft size={24} color={theme.colors.text} />
            </TouchableOpacity>
            <Text style={[styles.headerBarTitle, { color: theme.colors.text }]}>{displayName}</Text>
            {isAdmin ? (
              <TouchableOpacity onPress={openEditModal} style={styles.backBtn}>
                <Edit3 size={20} color={accentColor} />
              </TouchableOpacity>
            ) : (
              <View style={{ width: 32 }} />
            )}
          </View>

          <View style={styles.heroContent}>
            {/* Avatar */}
            <View style={[styles.avatar, { backgroundColor: accentColor }]}>
              <Text style={styles.avatarLetter}>{firstLetter}</Text>
            </View>

            <Text style={[styles.gymName, { color: theme.colors.text }]}>{displayName}</Text>

            {displayAddress && (
              <View style={styles.addressRow}>
                <MapPin size={14} color={theme.colors.textSecondary} />
                <Text style={[styles.addressText, { color: theme.colors.textSecondary }]}>{displayAddress}</Text>
              </View>
            )}

            {displayDesc ? (
              <Text style={[styles.description, { color: theme.colors.textSecondary }]}>{displayDesc}</Text>
            ) : null}

            <View style={[styles.memberBadge, { backgroundColor: accentColor + '22', borderColor: accentColor + '44' }]}>
              <Users size={14} color={accentColor} />
              <Text style={[styles.memberBadgeText, { color: accentColor }]}>
                {stats.totalMembers} {stats.totalMembers === 1 ? 'Member' : 'Members'}
              </Text>
            </View>
          </View>
        </View>

        {/* Stats Row */}
        <View style={styles.section}>
          <View style={styles.statsRow}>
            <StatCard icon={<Users size={18} color={accentColor} />} label="Members" value={stats.totalMembers} theme={theme} accent={accentColor} />
            <StatCard icon={<TrendingUp size={18} color={accentColor} />} label="Active (7d)" value={stats.activeThisWeek} theme={theme} accent={accentColor} />
            <StatCard icon={<Trophy size={18} color={accentColor} />} label="Avg Score" value={stats.avgStrengthScore} theme={theme} accent={accentColor} />
            <StatCard icon={<Dumbbell size={18} color={accentColor} />} label="Workouts (7d)" value={stats.totalWorkouts7d} theme={theme} accent={accentColor} />
          </View>
        </View>

        {/* Leaderboard Preview */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleRow}>
              <Crown size={18} color={accentColor} />
              <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Leaderboard</Text>
            </View>
            <TouchableOpacity onPress={() => router.push('/(tabs)/rank')}>
              <Text style={[styles.viewAllLink, { color: accentColor }]}>View Full Leaderboard →</Text>
            </TouchableOpacity>
          </View>
          <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            {leaderboard.length === 0 ? (
              <Text style={[styles.emptyCardText, { color: theme.colors.textTertiary }]}>No members yet</Text>
            ) : (
              leaderboard.map((entry, idx) => (
                <View
                  key={entry.user_id}
                  style={[
                    styles.leaderboardRow,
                    idx < leaderboard.length - 1 && { borderBottomWidth: 1, borderBottomColor: theme.colors.border },
                  ]}
                >
                  <View style={styles.leaderboardLeft}>
                    <Text style={[styles.rankNum, { color: idx < 3 ? accentColor : theme.colors.textTertiary }]}>
                      {idx + 1}
                    </Text>
                    <View style={[styles.leaderboardAvatar, { backgroundColor: theme.colors.surfaceVariant }]}>
                      <Text style={[styles.leaderboardAvatarText, { color: theme.colors.textSecondary }]}>
                        {entry.display_name.charAt(0).toUpperCase()}
                      </Text>
                    </View>
                    <View>
                      <Text style={[styles.leaderboardName, { color: theme.colors.text }]}>{entry.display_name}</Text>
                      {entry.handle && (
                        <Text style={[styles.leaderboardHandle, { color: theme.colors.textTertiary }]}>@{entry.handle}</Text>
                      )}
                    </View>
                  </View>
                  <Text style={[styles.leaderboardScore, { color: accentColor }]}>{entry.lifting_score}</Text>
                </View>
              ))
            )}
          </View>
        </View>

        {/* Active Challenges */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleRow}>
              <Swords size={18} color={accentColor} />
              <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Active Challenges</Text>
            </View>
          </View>
          <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            <Text style={[styles.emptyCardText, { color: theme.colors.textTertiary }]}>No active challenges</Text>
          </View>
        </View>

        {/* Announcements */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleRow}>
              <Megaphone size={18} color={accentColor} />
              <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Announcements</Text>
            </View>
          </View>
          <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            <Text style={[styles.emptyCardText, { color: theme.colors.textTertiary }]}>No announcements</Text>
          </View>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* Edit Modal */}
      <Modal visible={editModalVisible} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>Edit Gym Profile</Text>
              <TouchableOpacity onPress={() => setEditModalVisible(false)}>
                <X size={22} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <View style={styles.modalBody}>
              <Text style={[styles.fieldLabel, { color: theme.colors.textSecondary }]}>Gym Name</Text>
              <TextInput
                style={[styles.modalInput, { backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }]}
                value={editName}
                onChangeText={setEditName}
                placeholder="Gym name"
                placeholderTextColor={theme.colors.textTertiary}
              />

              <Text style={[styles.fieldLabel, { color: theme.colors.textSecondary }]}>Description</Text>
              <TextInput
                style={[styles.modalInput, styles.modalTextArea, { backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }]}
                value={editDesc}
                onChangeText={setEditDesc}
                placeholder="Tell members about your gym..."
                placeholderTextColor={theme.colors.textTertiary}
                multiline
                numberOfLines={3}
              />

              <Text style={[styles.fieldLabel, { color: theme.colors.textSecondary }]}>Accent Color</Text>
              <View style={styles.colorPalette}>
                {ACCENT_PALETTE.map((color) => (
                  <TouchableOpacity
                    key={color}
                    style={[
                      styles.colorSwatch,
                      { backgroundColor: color },
                      editColor === color && styles.colorSwatchSelected,
                    ]}
                    onPress={() => setEditColor(color)}
                  >
                    {editColor === color && <Check size={16} color="#fff" />}
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <TouchableOpacity
              style={[styles.primaryBtn, { backgroundColor: accentColor }, saving && { opacity: 0.6 }]}
              onPress={handleSaveEdit}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <Text style={[styles.primaryBtnText, { color: '#fff' }]}>Save Changes</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

function StatCard({ icon, label, value, theme, accent }: { icon: React.ReactNode; label: string; value: number; theme: any; accent: string }) {
  return (
    <View style={[statStyles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
      {icon}
      <Text style={[statStyles.value, { color: theme.colors.text }]}>{value}</Text>
      <Text style={[statStyles.label, { color: theme.colors.textTertiary }]}>{label}</Text>
    </View>
  );
}

const statStyles = StyleSheet.create({
  card: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 14,
    borderRadius: 2,
    borderWidth: 1,
    gap: 4,
  },
  value: {
    fontSize: 22,
    fontWeight: '800',
    fontFamily: 'Oswald_700Bold',
  },
  label: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
});

const styles = StyleSheet.create({
  container: { flex: 1 },
  center: { justifyContent: 'center', alignItems: 'center' },
  scrollView: { flex: 1 },
  scrollContent: { flexGrow: 1 },

  // Header bar
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 16,
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  backBtn: { padding: 4, width: 32, alignItems: 'center' },
  headerBarTitle: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 1,
    textTransform: 'uppercase',
    fontFamily: 'Oswald_700Bold',
  },

  // Hero
  heroSection: {
    paddingBottom: 24,
  },
  heroContent: {
    alignItems: 'center',
    paddingHorizontal: 20,
    gap: 8,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 4,
  },
  avatarLetter: {
    fontSize: 32,
    fontWeight: '800',
    color: '#fff',
    fontFamily: 'Oswald_700Bold',
  },
  gymName: {
    fontSize: 28,
    fontWeight: '800',
    fontFamily: 'Oswald_700Bold',
    textAlign: 'center',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  addressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  addressText: {
    fontSize: 13,
  },
  description: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 400,
  },
  memberBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 4,
    borderWidth: 1,
    marginTop: 4,
  },
  memberBadgeText: {
    fontSize: 13,
    fontWeight: '700',
  },

  // Sections
  section: {
    paddingHorizontal: 16,
    marginTop: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontFamily: 'Oswald_700Bold',
  },
  viewAllLink: {
    fontSize: 13,
    fontWeight: '600',
  },

  // Cards
  card: {
    borderRadius: 2,
    borderWidth: 1,
    overflow: 'hidden',
  },
  emptyCardText: {
    padding: 20,
    textAlign: 'center',
    fontSize: 14,
  },

  // Stats
  statsRow: {
    flexDirection: 'row',
    gap: 8,
  },

  // Leaderboard
  leaderboardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  leaderboardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  rankNum: {
    fontSize: 16,
    fontWeight: '800',
    width: 22,
    textAlign: 'center',
    fontFamily: 'Oswald_700Bold',
  },
  leaderboardAvatar: {
    width: 34,
    height: 34,
    borderRadius: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  leaderboardAvatarText: {
    fontSize: 14,
    fontWeight: '700',
  },
  leaderboardName: {
    fontSize: 14,
    fontWeight: '600',
  },
  leaderboardHandle: {
    fontSize: 12,
  },
  leaderboardScore: {
    fontSize: 18,
    fontWeight: '800',
    fontFamily: 'Oswald_700Bold',
  },

  // Empty state
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 22,
    fontWeight: '800',
    fontFamily: 'Oswald_700Bold',
    textTransform: 'uppercase',
  },
  emptySubtitle: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
  },

  // Buttons
  primaryBtn: {
    paddingVertical: 14,
    borderRadius: 2,
    alignItems: 'center',
    marginTop: 12,
  },
  primaryBtnText: {
    fontSize: 16,
    fontWeight: '700',
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 2,
    borderWidth: 1,
    padding: 24,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '800',
    fontFamily: 'Oswald_700Bold',
    textTransform: 'uppercase',
  },
  modalBody: {
    gap: 12,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  modalInput: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
  },
  modalTextArea: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  colorPalette: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  colorSwatch: {
    width: 36,
    height: 36,
    borderRadius: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  colorSwatchSelected: {
    borderWidth: 3,
    borderColor: '#fff',
  },
});
